import { Fragment, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Autocomplete, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Grid, IconButton, CircularProgress } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import ArgonBox from "components/ArgonBox";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import ArgonTypography from "components/ArgonTypography";
import CustomDatePicker from "components/CustomDatePicker/CustomDatePicker";
import api from "utils/api";

const initialForm = () => ({
  parentName: "", parentAddress: "", subject: "", additionalInfo: "",
  invoiceDate: dayjs().format("YYYY-MM-DD"), dueDate: dayjs().add(7, "day").format("YYYY-MM-DD"),
  quantity: "1", rate: "", discountPercent: "0", paymentMade: "0", terms: "Due on Receipt",
  notes: "UPI ID - 88108540@IDFCBANK",
  termsAndConditions: "The charged fee is non-refundable.\nPlease note that there will be a penalty of 100 rupees for delayed fees if not paid within 24 hours.",
});

const fields = [
  ["parentName", "Parent name", "text", true],
  ["parentAddress", "Parent address", "text", false, true],
  ["subject", "Subject / tuition description", "text", true, true],
  ["additionalInfo", "Session details / educator", "text", false, true],
  ["invoiceDate", "Invoice date", "date", true],
  ["dueDate", "Due date", "date", true],
  ["quantity", "Quantity", "number", true],
  ["rate", "Rate (INR)", "number", true],
  ["discountPercent", "Discount (%)", "number"],
  ["paymentMade", "Payment made (INR)", "number", true],
  ["terms", "Payment terms", "text"],
  ["notes", "Notes", "text", false, true],
  ["termsAndConditions", "Terms and conditions", "text", false, true],
];

const sectionTitles = {
  subject: "Tuition details",
  invoiceDate: "Dates & payment",
  notes: "Notes & terms",
};
const actionStyles = {
  px: { xs: 2, sm: 3 }, py: 2, gap: 1, flexWrap: "wrap",
  borderTop: "1px solid #e9ecef", backgroundColor: "#fff",
  "& > :not(style) ~ :not(style)": { ml: 0 },
  "& .MuiButton-root": { minHeight: 40, px: 2.5, flex: { xs: "1 1 auto", sm: "0 0 auto" } },
};

const decodeHtml = (value = "") => {
  const element = document.createElement("textarea"); element.innerHTML = value; return element.value;
};
const formFromInvoice = (invoice) => {
  if (!invoice) return initialForm();
  const snapshot = invoice.snapshot || {}; const item = snapshot.items?.[0] || {};
  return { parentName: invoice.parentName || decodeHtml(snapshot.parentName), parentAddress: decodeHtml(snapshot.parentAddress),
    subject: invoice.subject || decodeHtml(snapshot.subject), additionalInfo: decodeHtml(item.additionalInfo),
    invoiceDate: dayjs(invoice.invoiceDate).format("YYYY-MM-DD"), dueDate: dayjs(invoice.dueDate).format("YYYY-MM-DD"),
    quantity: String(item.quantity || 1), rate: String(item.rate || invoice.total || ""),
    discountPercent: String(snapshot.discountPercent || 0), paymentMade: String(invoice.paymentMade || 0),
    terms: decodeHtml(snapshot.terms || "Due on Receipt"), notes: decodeHtml(snapshot.notes || ""),
    termsAndConditions: decodeHtml(snapshot.termsAndConditions || "") };
};

export default function CreateInvoice({ initialInvoice = null, onClose: onEditClose, onSaved }) {
  const [open, setOpen] = useState(Boolean(initialInvoice));
  const [form, setForm] = useState(() => formFromInvoice(initialInvoice));
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [parents, setParents] = useState([]);
  const [selectedParent, setSelectedParent] = useState(null);
  const [parentSearch, setParentSearch] = useState("");
  const [parentsLoading, setParentsLoading] = useState(false);
  const [parentsError, setParentsError] = useState("");
  const requestKey = useRef("");
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview.url); }, [preview]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setParentsLoading(true);
    setParentsError("");
    setParents([]);
    const timer = window.setTimeout(async () => {
      try {
        const response = await api.get("/parent", { params: { search: parentSearch.trim(), limit: 100 } });
        if (active) setParents((response.data.data || []).filter((parent) => !parent.isDeleted));
      } catch (error) {
        if (active) setParentsError(error.response?.data?.message || error.response?.data?.msg || "Unable to load parents. Close and reopen this form to retry.");
      } finally {
        if (active) setParentsLoading(false);
      }
    }, 300);
    return () => { active = false; window.clearTimeout(timer); };
  }, [open, parentSearch]);

  const selectParent = (parent) => {
    setSelectedParent(parent);
    setForm((current) => ({
      ...current,
      parentName: parent?.fullName || "",
      parentAddress: parent ? [parent.address || parent.generatedMapAddress, parent.area, parent.city, parent.state, parent.pincode].filter(Boolean).join(", ") : "",
    }));
  };

  const subTotal = Math.round(Number(form.quantity) * Number(form.rate) * 100) / 100;
  const discountPercent = Number(form.discountPercent);
  const discountAmount = Math.round(subTotal * discountPercent) / 100;
  const total = Math.round((subTotal - discountAmount) * 100) / 100;
  const balance = Math.round((total - Number(form.paymentMade)) * 100) / 100;
  const money = (value) => `\u20B9${(Number.isFinite(value) ? value : 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const close = () => {
    if (busy) return;
    setOpen(false);
    setPreview(null);
    setForm(initialForm());
    setSelectedParent(null);
    setParentSearch("");
    if (initialInvoice) onEditClose?.();
  };
  const generate = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (!Number.isFinite(discountPercent) || discountPercent < 0 || discountPercent > 100) {
      toast.error("Discount must be between 0% and 100%.");
      return;
    }
    if (!form.parentName.trim() || !form.subject.trim() || !Number.isFinite(subTotal) || subTotal <= 0 || !Number.isFinite(total) || total < 0 || balance < 0 ||
        !dayjs(form.invoiceDate).isValid() || !dayjs(form.dueDate).isValid() || form.dueDate < form.invoiceDate) {
      toast.error("Check parent name, description, dates and amounts. Payment cannot exceed total.");
      return;
    }
    setBusy(true);
    try {
      if (!requestKey.current) requestKey.current = window.crypto.randomUUID();
      const response = initialInvoice
        ? await api.put(`/finance/manual-invoices/${initialInvoice._id}`, form).then((result) => ({ data: { data: initialInvoice, success: result.data.success } }))
        : await api.post("/finance/manual-invoices", { ...form, requestKey: requestKey.current });
      const invoice = response.data.data;
      const pdfResponse = await api.get(`/finance/manual-invoices/${invoice._id}/download`, { responseType: "blob" });
      setPreview({ url: URL.createObjectURL(new Blob([pdfResponse.data], { type: "application/pdf" })), filename: `${invoice.invoiceNumber}.pdf` });
      toast.success(`Invoice ${invoice.invoiceNumber} ${initialInvoice ? "updated" : "saved"} successfully.`);
      onSaved?.();
      requestKey.current = "";
    } catch (error) {
      let message = "Unable to generate invoice. Please try again.";
      if (error.response?.data instanceof Blob) {
        try { message = JSON.parse(await error.response.data.text()).message || message; } catch { /* Keep fallback message. */ }
      }
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!initialInvoice && <ArgonButton color="primary" variant="gradient" startIcon={<AddIcon />} onClick={() => setOpen(true)} sx={{ px: 3, minHeight: 42 }}>Create Invoice</ArgonButton>}
      <Dialog open={open} onClose={close} fullWidth maxWidth={preview ? "lg" : "md"}
        PaperProps={{ sx: { borderRadius: "16px", m: { xs: 1, sm: 3 }, width: { xs: "calc(100% - 16px)", sm: "100%" }, maxHeight: "calc(100% - 32px)" } }}>
        <DialogTitle component="div" sx={{ px: { xs: 2, sm: 3 }, py: 2.5, borderBottom: "1px solid #e9ecef" }}>
          <ArgonBox display="flex" alignItems="center" justifyContent="space-between" gap={2}>
            <ArgonBox display="flex" alignItems="center" gap={1.5}>
              <ArgonBox bgColor="primary" variant="gradient" color="white" borderRadius="lg" display="flex" alignItems="center" justifyContent="center" width={44} height={44}>
                <ReceiptLongIcon fontSize="small" />
              </ArgonBox>
              <ArgonBox>
            <ArgonTypography variant="h6" fontWeight="medium">{preview ? "Invoice preview" : initialInvoice ? "Edit Invoice" : "Create Invoice"}</ArgonTypography>
                <ArgonTypography variant="caption" color="text">{preview ? "Review your invoice and download the PDF." : "Prepare an invoice to share with the parent."}</ArgonTypography>
              </ArgonBox>
            </ArgonBox>
            <IconButton aria-label="Close invoice" onClick={close} disabled={busy} size="small"><CloseIcon /></IconButton>
          </ArgonBox>
        </DialogTitle>
        {preview ? (
          <>
            <DialogContent>
              <iframe title="Invoice preview" src={preview.url} style={{ width: "100%", height: "70vh", border: 0 }} />
            </DialogContent>
            <DialogActions sx={actionStyles}>
              <ArgonButton color="secondary" variant="outlined" startIcon={<EditOutlinedIcon />} onClick={() => setPreview(null)}>Edit details</ArgonButton>
              <ArgonButton color="primary" variant="outlined" startIcon={<OpenInNewIcon />} component="a" href={preview.url} target="_blank" rel="noopener noreferrer">Open</ArgonButton>
              <ArgonButton color="primary" variant="gradient" startIcon={<DownloadIcon />} component="a" href={preview.url} download={preview.filename}>Download PDF</ArgonButton>
            </DialogActions>
          </>
        ) : (
          <ArgonBox component="form" onSubmit={generate} display="flex" flexDirection="column" sx={{ minHeight: 0, overflow: "hidden" }}>
            <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
              <ArgonBox mb={3} px={2} py={1.25} borderRadius="lg" sx={{ backgroundColor: "#f4f6ff", border: "1px solid #e4e8ff" }}>
              <ArgonTypography variant="caption" color="text">{initialInvoice ? "Update the saved invoice details and PDF." : "This invoice is only for download and will not be saved."}</ArgonTypography>
              </ArgonBox>
              <ArgonTypography variant="h6" fontWeight="medium" mb={2}>Parent details</ArgonTypography>
              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <ArgonTypography component="label" htmlFor="manual-invoice-parent" variant="body2" fontWeight="medium" display="block" mb={1}>
                    Select parent
                  </ArgonTypography>
                  <Autocomplete
                    id="manual-invoice-parent"
                    options={parents}
                    value={selectedParent}
                    disabled={busy}
                    loading={parentsLoading}
                    filterOptions={(options) => options}
                    getOptionLabel={(parent) => [parent.fullName, parent.phone || parent.email].filter(Boolean).join(" - ")}
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                    onChange={(_, parent) => selectParent(parent)}
                    onInputChange={(_, value, reason) => {
                      if (reason === "input") setParentSearch(value);
                      if (reason === "clear" || reason === "reset") setParentSearch("");
                    }}
                    noOptionsText={parentsError ? "Unable to load parents" : "No parents found. Try searching by name or phone."}
                    renderOption={(props, parent) => (
                      <li {...props} key={parent._id}>
                        <ArgonBox>
                          <ArgonTypography variant="button" fontWeight="medium">{parent.fullName}</ArgonTypography>
                          <ArgonTypography variant="caption" color="secondary" display="block">{[parent.phone, parent.email].filter(Boolean).join(" · ")}</ArgonTypography>
                        </ArgonBox>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Search parent by name, phone or email" size="small"
                        error={Boolean(parentsError)} helperText={parentsError || "Select a parent to fill their name and address, or enter details below."} />
                    )}
                  />
                </Grid>
                {fields.map(([key, label, type, required, multiline]) => (
                  <Fragment key={key}>
                  {sectionTitles[key] && (
                    <Grid item xs={12}>
                      <ArgonBox pt={2.5} mt={0.5} sx={{ borderTop: "1px solid #e9ecef" }}>
                        <ArgonTypography variant="h6" fontWeight="medium">{sectionTitles[key]}</ArgonTypography>
                      </ArgonBox>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6} md={["quantity", "rate", "discountPercent", "paymentMade"].includes(key) ? 3 : 6}>
                    <ArgonTypography component="label" htmlFor={`manual-invoice-${key}`} variant="body2" fontWeight="medium" display="block" mb={1}>
                      {label}{required ? " *" : ""}
                    </ArgonTypography>
                    {type === "date" ? (
                      <CustomDatePicker label="" width="100%" disabled={busy}
                        value={form[key] ? dayjs(form[key]) : null}
                        minDate={key === "dueDate" && dayjs(form.invoiceDate).isValid() ? dayjs(form.invoiceDate) : undefined}
                        popperSx={(theme) => ({ zIndex: theme.zIndex.modal + 100 })}
                        onChange={(value) => setForm((current) => ({ ...current, [key]: value?.isValid() ? value.format("YYYY-MM-DD") : "" }))} />
                    ) : (
                    <ArgonInput id={`manual-invoice-${key}`} fullWidth type={type} required={required}
                      multiline={Boolean(multiline)} minRows={multiline ? 2 : undefined}
                      placeholder={key === "subject" ? "e.g. Home Tuition for Class 8 - Maths" : key === "additionalInfo" ? "Sessions, duration and educator name" : key === "rate" ? "0.00" : undefined}
                      disabled={busy} value={form[key]}
                      inputProps={type === "number" ? { min: ["paymentMade", "discountPercent"].includes(key) ? 0 : 0.01, step: "0.01", max: key === "discountPercent" ? 100 : key === "paymentMade" ? total : undefined } :
                        type === "date" ? { min: key === "dueDate" ? form.invoiceDate : undefined } :
                        { maxLength: key === "parentName" || key === "terms" ? 200 : key === "subject" ? 500 : key === "parentAddress" ? 2000 : key === "termsAndConditions" ? 5000 : 3000 }}
                      onChange={(event) => setForm((current) => ({ ...current, [key]: event.target.value }))} />
                    )}
                    {key === "paymentMade" && <ArgonTypography variant="caption" color={balance < 0 ? "error" : "text"} display="block" mt={0.5}>{balance < 0 ? "Payment cannot exceed the invoice total." : "Enter the amount already received. Use 0 if unpaid."}</ArgonTypography>}
                    {key === "discountPercent" && <ArgonTypography variant="caption" color="text" display="block" mt={0.5}>0–100%. Applied to Quantity × Rate.</ArgonTypography>}
                  </Grid>
                  </Fragment>
                ))}
              </Grid>
              <ArgonBox mt={3} p={2.5} borderRadius="lg" sx={{ backgroundColor: "#f8f9fa", border: "1px solid #e9ecef" }}>
                <ArgonTypography variant="h6" fontWeight="medium" mb={2}>Invoice summary</ArgonTypography>
                <Grid container spacing={2}>
                  {[["Sub total", subTotal], [`Discount (${discountPercent || 0}%)`, discountAmount], ["Invoice total", total], ["Payment made", Number(form.paymentMade)], ["Due amount", balance]].map(([label, amount]) => (
                    <Grid item xs={12} sm={4} key={label}>
                      <ArgonTypography variant="caption" color="text" display="block">{label}</ArgonTypography>
                      <ArgonTypography variant="h5" fontWeight="bold" color={label === "Due amount" ? balance < 0 ? "error" : "primary" : "dark"}>{money(amount)}</ArgonTypography>
                    </Grid>
                  ))}
                </Grid>
                <ArgonBox mt={2} pt={1.5} sx={{ borderTop: "1px solid #e9ecef" }}>
                  <ArgonTypography variant="caption" color="text" display="block">Sub total = Quantity × Rate. Total = Sub total − Discount. Due amount = Total − Payment made.</ArgonTypography>
                  <ArgonTypography variant="caption" color="text" display="block">{money(total)} − {money(Number(form.paymentMade))} = {money(balance)}. Only the amounts entered in this form are included.</ArgonTypography>
                </ArgonBox>
              </ArgonBox>
            </DialogContent>
            <DialogActions sx={actionStyles}>
              <ArgonButton color="secondary" variant="outlined" onClick={close} disabled={busy}>Cancel</ArgonButton>
              <ArgonButton type="submit" color="primary" variant="gradient" disabled={busy}
                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <ReceiptLongIcon />}>
                {busy ? "Generating..." : initialInvoice ? "Save & Open Invoice" : "Create & Open Invoice"}
              </ArgonButton>
            </DialogActions>
          </ArgonBox>
        )}
      </Dialog>
    </>
  );
}

CreateInvoice.propTypes = { initialInvoice: PropTypes.object, onClose: PropTypes.func, onSaved: PropTypes.func };
